import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { validateText, validatePostalCode } from '../utils/inputValidation';
import { SOUTH_AFRICAN_PROVINCES } from '../constants/addressConstants';

// Helper function to match imported province to standard list
const normalizeProvince = (importedProvince) => {
  if (!importedProvince) return '';

  const normalized = importedProvince.trim().toLowerCase();

  // Find matching province (case-insensitive, handles common variations)
  const match = SOUTH_AFRICAN_PROVINCES.find(prov => {
    const provLower = prov.toLowerCase();
    return provLower === normalized ||
           provLower.replace(/[-\s]/g, '') === normalized.replace(/[-\s]/g, '') ||
           // Handle common variations
           (normalized.includes('kwa') && normalized.includes('natal') && prov === 'KwaZulu-Natal') ||
           (normalized.includes('north') && normalized.includes('west') && prov === 'North West') ||
           (normalized.includes('free') && prov === 'Free State') ||
           (normalized.includes('eastern') && prov === 'Eastern Cape') ||
           (normalized.includes('western') && prov === 'Western Cape') ||
           (normalized.includes('northern') && normalized.includes('cape') && prov === 'Northern Cape');
  });

  return match || importedProvince; // Return original if no match found
};

const AddressModal = ({ show, onHide, onSuccess }) => {
  const { currentUser, fetchUserProfile, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    streetAddress: '',
    city: '',
    province: '',
    postalCode: ''
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Update form data when userProfile changes or modal opens
  useEffect(() => {
    if (show && userProfile) {
      setFormData({
        streetAddress: userProfile.streetAddress || '',
        city: userProfile.city || '',
        province: normalizeProvince(userProfile.province),
        postalCode: userProfile.postalCode || ''
      });
      // Clear any previous errors when modal opens
      setErrors({});
      setError('');
    }
  }, [show, userProfile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Street address validation - more lenient
    const streetAddress = formData.streetAddress?.trim() || '';
    if (!streetAddress) {
      newErrors.streetAddress = 'Please enter your street address';
    } else if (streetAddress.length < 5) {
      newErrors.streetAddress = 'Street address must be at least 5 characters';
    } else if (streetAddress.length > 200) {
      newErrors.streetAddress = 'Street address is too long (max 200 characters)';
    }

    // City validation - more lenient
    const city = formData.city?.trim() || '';
    if (!city) {
      newErrors.city = 'Please enter your city or town';
    } else if (city.length < 2) {
      newErrors.city = 'City name must be at least 2 characters';
    }

    // Province validation
    if (!formData.province) {
      newErrors.province = 'Please select your province';
    } else if (!SOUTH_AFRICAN_PROVINCES.includes(formData.province)) {
      newErrors.province = 'Please select a valid province from the list';
    }

    // Postal code validation - more lenient
    const postalCode = formData.postalCode?.trim() || '';
    if (!postalCode) {
      newErrors.postalCode = 'Please enter your postal code';
    } else if (!/^\d{4}$/.test(postalCode)) {
      newErrors.postalCode = 'Postal code must be exactly 4 digits (e.g., 2000)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please fix the errors above before continuing');
      return;
    }

    setLoading(true);

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const updateData = {
        streetAddress: formData.streetAddress.trim(),
        city: formData.city.trim(),
        province: formData.province,
        postalCode: formData.postalCode.trim(),
        addressLastUpdated: new Date().toISOString(),
        // Clear the verification flag since address is now complete
        needsAddressVerification: false
      };

      // Only set addressCompletedAt if this is the first time
      if (!userProfile?.addressCompletedAt) {
        updateData.addressCompletedAt = new Date().toISOString();
      }

      await updateDoc(userRef, updateData);

      // Refresh user profile in AuthContext
      await fetchUserProfile(currentUser);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onHide();
    } catch (err) {
      devError('Error updating address:', err);
      setError('Failed to save address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Don't allow closing without completing address (make it required)
    // User can only close by clicking "Save Address" button
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>Complete Your Address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <strong>Required:</strong> Please verify and complete your physical address to continue.
          <p className="mb-0 mt-2">
            This information is required for course materials and official correspondence.
            All fields are required.
          </p>
        </Alert>

        {error && (
          <Alert variant="danger">
            <strong>Please fix the following:</strong> {error}
          </Alert>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert variant="warning">
            <strong>Some fields need attention:</strong>
            <ul className="mb-0 mt-2">
              {Object.entries(errors).map(([field, msg]) => (
                <li key={field}>{msg}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="streetAddress">
                <Form.Label>
                  Street Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your street address (e.g., 123 Main Street, Apt 4)"
                  value={formData.streetAddress}
                  onChange={(e) => handleChange('streetAddress', e.target.value)}
                  isInvalid={!!errors.streetAddress}
                  required
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.streetAddress}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Include your street number, street name, and unit/apartment if applicable
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="city">
                <Form.Label>
                  City/Town <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your city or town"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  isInvalid={!!errors.city}
                  required
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="province">
                <Form.Label>
                  Province <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={formData.province}
                  onChange={(e) => handleChange('province', e.target.value)}
                  isInvalid={!!errors.province}
                  required
                  disabled={loading}
                >
                  <option value="">Select province...</option>
                  {SOUTH_AFRICAN_PROVINCES.map(prov => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.province}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="postalCode">
                <Form.Label>
                  Postal Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 2000"
                  value={formData.postalCode}
                  onChange={(e) => handleChange('postalCode', e.target.value)}
                  isInvalid={!!errors.postalCode}
                  maxLength={4}
                  pattern="\d{4}"
                  required
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.postalCode}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  4-digit South African postal code
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-4 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Need help? Contact support at support@npigovernance.co.za
            </small>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              size="lg"
            >
              {loading ? 'Saving...' : 'Save Address'}
            </Button>
          </div>
        </Form>

        <Alert variant="light" className="mt-3 border">
          <small>
            <strong>Tips:</strong>
            <ul className="mb-0 mt-1">
              <li><strong>Street Address:</strong> Include your house/unit number and street name (e.g., "42 Main Road, Unit 5")</li>
              <li><strong>Postal Code:</strong> Must be exactly 4 digits (e.g., 2000 for Johannesburg)</li>
            </ul>
          </small>
        </Alert>
      </Modal.Body>
    </Modal>
  );
};

export default AddressModal;
